import {
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  RefObject,
} from "react";
import { useMutation, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import i18n from "i18next";
import { format } from "date-fns";
import { useProfile } from "@contexts/ProfileContext";
import { getRegions, getValidation } from "@api/apiClient/address";
import { getAddresses, deleteAddress, AddressTemplate } from "@api/apiClient";
import ConfirmModal from "@components/modals/ConfirmModal";
// import { Title2 } from "@components/new/Typo";
import { Accordeon } from "@components/new/Accordeon";
import { Alert, AlertType } from "@components/new/Alert";
import SelectContainer from "@containers/SelectContainer";
import DateInputContainer from "@containers/DateInputContainer";
import { actionAddresses } from "@utils/handlerSubmitActions";
import getFullAddress from "@utils/getFullAddress";
import { weightKgValue, formatSumWithCurrency } from "@utils/formatValues";
import {
  validateAddress,
  getFBPRequestDetails,
  getFBPPickupTariff,
  IFBPPickupRequestData,
} from "@api/apiClient/fbp";
import Calculation from "./Calculation";
import PlacingOrderForm from "../../components/PlacingOrderForm/PlacingOrderForm";
import { FormHandle, AddrFormHandle } from "./types";
import { emptyAddress } from "../../utils/emptyAddress";
import {
  getPickUpFirstDate,
  getPickUpLastDate,
  getTimeSlots,
} from "@utils/getChineseDate";
import { TUpdateFormValue, TPlacingOrderDataItem } from "../../placing-types";
import DelIcon2 from "@assets/icons/solid/trash-fill.svg?react";
import styles from "./Form.module.scss";

export interface FormType extends AddressTemplate {
  pickUpTime: string;
  pickUpDate: string;
  rememberAddress: boolean;
}

enum AddressValidationState {
  Idle = "idle",
  Pending = "pending",
  Valid = "valid",
  Invalid = "invalid",
}

const Form = ({
  formRef,
  onStatusChange,
  onSubmitPayload,
  restockRequestId,
  fbpWarehouseId,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean;
    isLoading?: boolean;
    isAddressInvalid?: boolean;
    isInsufficient?: boolean;
    missingAmount?: number;
    currency?: string;
  }) => void;
  onSubmitPayload: (payload: IFBPPickupRequestData) => void;
  restockRequestId: string;
  fbpWarehouseId: string;
  // createdAt: string;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const { account } = useProfile();
  // Feature FreeFbpPickUp: пикап для FBP бесплатный — не показываем тариф,
  // расчёт стоимости и никак не обозначаем, что пикап платный.
  const isFreeFbpPickUp = Boolean(account?.features?.freeFbpPickUp);
  const addrRef = useRef<AddrFormHandle>({ submit: () => false });
  const validateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [addressIdToDelete, setAddressIdToDelete] = useState<string>("");
  const [isConfirmDeleteAddressOpen, setConfirmDeleteAddressOpen] =
    useState<boolean>(false);
  const [accordeonActiveId, setAccordeonActiveId] =
    useState<string>("dateTime");
  const [addressValidationState, setAddressValidationState] =
    useState<AddressValidationState>(AddressValidationState.Idle);

  const { control, handleSubmit, watch, setValues, setValue, getValues } =
    useForm<FormType>({
      defaultValues: {
        pickUpTime: "",
        pickUpDate: "",
        rememberAddress: true,
        ...emptyAddress,
      },
      // mode: 'onBlur',
      // reValidateMode: 'onBlur',
    });

  const pickUpTime = watch("pickUpTime");
  const pickUpDate = watch("pickUpDate");
  const rememberAddress = watch("rememberAddress");

  const {
    data: dataGetValidation,
    // isLoading: isLoadingGetValidation,
    mutate: mutateGetValidation,
    // reset: resetGetValidation
  } = useMutation({
    mutationFn: getValidation,
  });

  const {
    data: dataGetRegions,
    // isLoading: isLoadingGetRegions
  } = useQuery({
    queryKey: ["getRegions"],
    queryFn: () => getRegions(),
    retry: 1,
    // enabled: account!.region === 'CN'
  });

  const {
    data: dataGetAddresses,
    // isLoading: isLoadingGetAddresses,
    refetch: refetchGetAddresses,
  } = useQuery({
    queryKey: ["getAddresses"],
    queryFn: () => getAddresses(),
    retry: 1,
  });

  const deleteAddressMutation = useMutation({
    mutationFn: deleteAddress,
    onSuccess: () => {
      refetchGetAddresses().then(() => {
        setTimeout(() => {
          // setCurrentTypeModalWindow('placing');
        }, 300);
      });
    },
  });

  // const getIsValid = () => {
  //   return true;
  // }

  const { mutateAsync: mutateValidateAddress, isLoading: isValidatingAddress } =
    useMutation({
      mutationFn: validateAddress,
    });

  const { data: requestDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["getFBPRequestDetails", restockRequestId],
    queryFn: () => getFBPRequestDetails(restockRequestId),
    retry: 1,
    enabled: Boolean(restockRequestId),
  });

  const { data: tariff, isLoading: isLoadingTariff } = useQuery({
    queryKey: ["getFBPPickupTariff", fbpWarehouseId],
    queryFn: () => getFBPPickupTariff(fbpWarehouseId),
    retry: 1,
    enabled: Boolean(fbpWarehouseId) && !isFreeFbpPickUp,
  });

  // Общий вес заявки в кг = сумма по всем товарам weight * count.
  const totalWeightKg =
    (requestDetails?.ymItems ?? []).reduce(
      (acc, v) => acc + weightKgValue(v.ymProduct.weight) * (v.count ?? 0),
      0,
    ) +
    (requestDetails?.wbItems ?? []).reduce(
      (acc, v) => acc + weightKgValue(v.wbProduct.weight) * (v.count ?? 0),
      0,
    ) +
    (requestDetails?.aliItems ?? []).reduce(
      (acc, v) => acc + weightKgValue(v.aliProduct.weight) * (v.count ?? 0),
      0,
    );

  const balance = account?.balance ?? 0;
  const pricePerKg = tariff?.pricePerKg ?? 0;
  const currency = tariff?.currency ?? "CNY";
  const amountDue = totalWeightKg * pricePerKg;
  const isCalculationLoading = isLoadingDetails || isLoadingTariff;
  // При бесплатном пикапе оплата не требуется — сценарий пополнения не включается.
  const isInsufficient =
    !isFreeFbpPickUp && amountDue > 0 && balance < amountDue;

  useEffect(() => {
    onStatusChange({
      disabled:
        !pickUpTime ||
        !pickUpDate ||
        addressValidationState !== AddressValidationState.Valid,
      isLoading: Boolean(isValidatingAddress),
      isAddressInvalid:
        addressValidationState === AddressValidationState.Invalid,
      isInsufficient,
      missingAmount: Math.max(amountDue - balance, 0),
      currency,
    });
  }, [
    isValidatingAddress,
    currentLanguage,
    pickUpTime,
    pickUpDate,
    addressValidationState,
    isInsufficient,
    amountDue,
    balance,
    currency,
  ]);

  // Когда выбраны дата и время — автоматически раскрываем секцию ввода адреса.
  useEffect(() => {
    if (pickUpDate && pickUpTime) {
      setAccordeonActiveId("addr");
    }
  }, [pickUpDate, pickUpTime]);

  const onSubmit = async () => {
    const isAddressFormValid = Boolean(
      addrRef.current && addrRef.current.submit(),
    );
    const form = getValues();

    if (!isAddressFormValid) {
      setAccordeonActiveId("addr");
      return;
    }

    // const [hour, minute] = form.pickUpTime.split(":").map(v => +v);
    onSubmitPayload({
      address: {
        rawLine: form.address.rawLine ?? "",
        street: form.address.street ?? "",
        city: form.address.city ?? "",
        cityDistrict: form.address.cityDistrict ?? "",
        settlement: form.address.settlement ?? "",
        country: form.address.country ?? "",
        countryCode: form.address.countryCode ?? "",
        zipCode: form.address.zipCode ?? "",
        region: form.address.region ?? "",
        area: form.address.area ?? "",
        house: form.address.house ?? "",
        flat: form.address.flat ?? "",
      },
      contactPhone: form.phone ?? "",
      contactName: form.fullName ?? "",
      shipmentDate: format(new Date(form.pickUpDate), "yyyy-MM-dd"),
      shipmentTime: `${form.pickUpTime?.length > 4 ? form.pickUpTime : "0" + form.pickUpTime}:00`,
      restockRequestId,
      comment: form.comment ?? "",
    });

    actionAddresses([form] as TPlacingOrderDataItem[]);
  };

  const submit = handleSubmit(onSubmit);
  useImperativeHandle(formRef, () => ({
    submit,
  }));

  const onClickDeleteAddress = (id: string): void => {
    console.log(id);
    setAddressIdToDelete(id);
    setConfirmDeleteAddressOpen(true);
  };

  const updateForm = (
    _: string,
    propertyPath: string,
    value: TUpdateFormValue,
  ): void => {
    if (typeof value === "boolean" || value instanceof Date) {
      // @ts-ignore
      setValue(propertyPath, value);
    } else {
      setValues({
        pickUpDate,
        pickUpTime,
        ...value,
      });

      const address = (value as AddressTemplate)?.address;
      const addressEntered = Boolean(
        address?.region &&
          address?.cityDistrict &&
          address?.street &&
          address?.house &&
          address?.zipCode,
      );

      if (validateDebounceRef.current != null)
        clearTimeout(validateDebounceRef.current);

      if (!addressEntered) {
        setAddressValidationState(AddressValidationState.Idle);
        return;
      }

      setAddressValidationState(AddressValidationState.Pending);
      validateDebounceRef.current = setTimeout(async () => {
        try {
          const { isValid } = await mutateValidateAddress({
            addressRawLine: getFullAddress(address!),
          });
          setAddressValidationState(
            isValid
              ? AddressValidationState.Valid
              : AddressValidationState.Invalid,
          );
        } catch {
          setAddressValidationState(AddressValidationState.Idle);
        }
      }, 600);
    }
  };

  const views = [
    {
      title: t("fbpPage.pickUp.dateAndTime"),
      id: "dateTime",
      content: (
        <div className={styles.section}>
          <div className={styles.sectionRow}>
            <DateInputContainer
              control={control}
              name="pickUpDate"
              placeholder=""
              title={t("fbpPage.pickUp.date")}
              rules={{ required: t("defaultFormErrors.required") }}
              disabledBefore={getPickUpFirstDate()}
              disabledAfter={getPickUpLastDate()}
            />
            <SelectContainer<{ id: string; value: string }, FormType>
              control={control}
              name="pickUpTime"
              title={t("fbpPage.pickUp.time")}
              placeholder={t("fbpPage.pickUp.selectTime")}
              items={getTimeSlots(pickUpDate).map((v) => ({ id: v, value: v }))}
              keyExtractor={(item) => item?.id}
              labelOptionItem={(item) => item?.value ?? ""}
              rules={{ required: t("defaultFormErrors.required") }}
              disabled={!pickUpDate}
            />
          </div>
        </div>
      ),
    },
    {
      title: t("fbpPage.pickUp.address"),
      id: "addr",
      content: (
        <div className={styles.section}>
          <PlacingOrderForm
            formRef={addrRef}
            country="CN"
            dataGetValidation={dataGetValidation}
            handlerMutateGetValidation={mutateGetValidation}
            handlerOnClickDeleteAddress={onClickDeleteAddress}
            handlerUpdateForm={updateForm}
            withFullAddressInEnglish={false}
            showComment
            showEmail={false}
            showOneCountryPhone={false}
            addressTemplates={dataGetAddresses?.addressTemplates ?? []}
            rememberAddress={rememberAddress}
            regions={dataGetRegions?.provinces ?? []}
            // values?: AddressTemplate | undefined;
            // contactMinLength: number | undefined;
            // contactPlaceholder: string;
            // isNew: boolean | undefined;
            // title: string;
            // description: string | string[] | React.ReactNode | React.ReactNode[] | undefined;
            // shipmentDates: TPlacingOrderShipmentDates;
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.form}>
      <Alert
        type={AlertType.NEUTRAL}
        title={
          isFreeFbpPickUp
            ? t("fbpPage.pickUp.alertTitleFree")
            : t("fbpPage.pickUp.alertTitle", {
                price: formatSumWithCurrency(pricePerKg, currency),
              })
        }
        description={t("fbpPage.pickUp.alertDescription")}
      />
      <div className={styles.contentRow}>
        <div className={styles.leftColumn}>
          <Accordeon
            defaultOpenId={accordeonActiveId}
            items={views}
            className={styles.accordeon}
            // stretch
            // onChange={setAccordeonPage}
          />
        </div>
        {!isFreeFbpPickUp && (
          <Calculation
            balance={balance}
            totalWeightKg={totalWeightKg}
            pricePerKg={pricePerKg}
            currency={currency}
            amountDue={amountDue}
            isInsufficient={isInsufficient}
            isLoading={isCalculationLoading}
          />
        )}
      </div>

      {isConfirmDeleteAddressOpen ? (
        <ConfirmModal
          key={addressIdToDelete}
          title={t("fbpPage.removeAddress.title")}
          // description={t("fbpPage.removeAddress.description")}
          noBtnTitle={t("fbpPage.removeAddress.cancelBtn")}
          yesBtnTitle={t("fbpPage.removeAddress.okBtn")}
          onClose={() => setConfirmDeleteAddressOpen(false)}
          requestFn={() => deleteAddressMutation.mutate(addressIdToDelete)}
          onSuccess={() => setConfirmDeleteAddressOpen(false)}
          icon={<DelIcon2 />}
        />
      ) : null}
    </div>
  );
};

export default Form;
